import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { companySettingsService } from '../services/companySettingsService';
import { PrintOutputType } from '../types';
import { useAuth } from './AuthContext';

// ── Types ─────────────────────────────────────────────────────────────────────

interface CompanyContextType {
  /** Full URL of the company logo, or null if none is set. */
  logoUrl: string | null;
  /** ISO 4217 currency code from company settings, e.g. 'LKR', 'USD'. */
  currencyCode: string;
  /** How the Print Bill button should behave: PDF (browser tab) or POS_PRINTER. */
  defaultPrintOutput: PrintOutputType;
  /** Name of the configured POS printer, if any. */
  defaultPrinterName: string | null;
  /** Re-fetch company settings (call after logo upload or settings save). */
  refreshCompany: () => Promise<void>;
}

// ── Context ───────────────────────────────────────────────────────────────────

const CURRENCY_STORAGE_KEY = 'company_currency_code';

const PRINT_OUTPUT_STORAGE_KEY = 'company_print_output';
const PRINTER_NAME_STORAGE_KEY  = 'company_printer_name';

const CompanyContext = createContext<CompanyContextType>({
  logoUrl: null,
  currencyCode: localStorage.getItem(CURRENCY_STORAGE_KEY) || 'USD',
  defaultPrintOutput: (Number(localStorage.getItem(PRINT_OUTPUT_STORAGE_KEY)) as PrintOutputType) ?? PrintOutputType.PDF,
  defaultPrinterName: localStorage.getItem(PRINTER_NAME_STORAGE_KEY),
  refreshCompany: async () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────

const LOGO_STORAGE_KEY = 'company_logo_url';

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();

  // Initialise from localStorage to avoid any flash on page load
  const [logoUrl, setLogoUrl] = useState<string | null>(() =>
    localStorage.getItem(LOGO_STORAGE_KEY),
  );
  const [currencyCode, setCurrencyCode] = useState<string>(() =>
    localStorage.getItem(CURRENCY_STORAGE_KEY) || 'USD',
  );
  const [defaultPrintOutput, setDefaultPrintOutput] = useState<PrintOutputType>(() =>
    (Number(localStorage.getItem(PRINT_OUTPUT_STORAGE_KEY)) as PrintOutputType) ?? PrintOutputType.PDF,
  );
  const [defaultPrinterName, setDefaultPrinterName] = useState<string | null>(() =>
    localStorage.getItem(PRINTER_NAME_STORAGE_KEY),
  );

  const refreshCompany = useCallback(async () => {
    try {
      // Uses the lightweight logo-url endpoint — any authenticated user, no
      // Settings.View permission required.  Returns both logoUrl and currencyCode.
      const res = await companySettingsService.getLogoUrl();
      const data = res.data?.data;

      // ── Logo ──────────────────────────────────────────────────────────────
      const url = data?.logoUrl ?? null;
      setLogoUrl(url);
      if (url) {
        localStorage.setItem(LOGO_STORAGE_KEY, url);
      } else {
        localStorage.removeItem(LOGO_STORAGE_KEY);
      }

      // ── Currency ──────────────────────────────────────────────────────────
      const code = data?.currencyCode;
      if (code && code.trim().length > 0) {
        setCurrencyCode(code);
        localStorage.setItem(CURRENCY_STORAGE_KEY, code);
      }

      // ── Print output mode ─────────────────────────────────────────────────
      if (data?.defaultPrintOutput !== undefined && data.defaultPrintOutput !== null) {
        const mode = data.defaultPrintOutput as PrintOutputType;
        setDefaultPrintOutput(mode);
        localStorage.setItem(PRINT_OUTPUT_STORAGE_KEY, String(mode));
      }
      const printerName = data?.defaultPrinterName ?? null;
      setDefaultPrinterName(printerName);
      if (printerName) {
        localStorage.setItem(PRINTER_NAME_STORAGE_KEY, printerName);
      } else {
        localStorage.removeItem(PRINTER_NAME_STORAGE_KEY);
      }
    } catch {
      // Silently ignore — components fall back to cached/default values
    }
  }, []);

  // Only fetch when authenticated — mirrors the same guard in TillProvider.
  // Without this check, an unauthenticated mount (e.g. on the login page)
  // fires a 401, which the interceptor turns into a window.location redirect,
  // causing an infinite reload loop.
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    refreshCompany();
  }, [isAuthenticated, user?.id, refreshCompany]);

  return (
    <CompanyContext.Provider value={{ logoUrl, currencyCode, defaultPrintOutput, defaultPrinterName, refreshCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export const useCompany = (): CompanyContextType => useContext(CompanyContext);
